import { useEffect } from "react";
import { SITE_URL } from "./content-defaults";

/** Inyecta un bloque <script type="application/ld+json"> en el <head> mientras el componente
 *  que lo llama esté montado — Google (y cada vez más asistentes de IA) lo usan para entender
 *  de qué trata la página en un formato estructurado, sin tener que "adivinar" leyendo el
 *  texto visible. Se identifica con un id propio para no duplicarlo si el contenido cambia
 *  (ej. cuando useContent termina de cargar desde Firestore y actualiza los datos), y se saca
 *  del <head> recién cuando el componente se desmonta de verdad — separado en dos efectos a
 *  propósito: el primero corre en cada cambio de dato (crea o actualiza), el segundo (deps
 *  fijas) es el único que hace la limpieza final, para no borrar el script a mitad de una
 *  actualización de contenido. */
export function useJsonLd(id: string, data: object | null) {
  useEffect(() => {
    if (!data) return;
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, JSON.stringify(data)]);

  useEffect(() => {
    return () => { document.getElementById(id)?.remove(); };
  }, [id]);
}

/** JSON-LD de una lista de preguntas frecuentes (schema.org FAQPage) — mismo shape {q, a} que
 *  ya usan home_faqs/kidstenia_faqs en content-defaults.ts, para no tener que transformar nada
 *  al llamar esto desde cada página. */
export function faqPageJsonLd(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/** JSON-LD de las migas de pan (schema.org BreadcrumbList) de una página — le muestra a Google
 *  la ubicación de la página dentro del sitio (a veces se ve como "cprogresiva.cl > Clases" en
 *  vez de la URL pelada en el resultado de búsqueda). Es solo el dato estructurado: no agrega
 *  ninguna miga de pan visible ni cambia el diseño de la página. `items` va desde Inicio hasta
 *  la página actual, ej. [{name: "Inicio", path: "/"}, {name: "Clases", path: "/clases"}]. */
export function breadcrumbJsonLd(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}
